import { inject, Injectable } from '@angular/core';
import { LpActionsService } from '@features/lp/lp-actions/lp-actions.service';
import { LpEvent, LpTrigger, LpTriggerListener } from '@typings/lp/generated';

@Injectable({
  providedIn: 'root',
})
export class LpTriggersService {
  readonly #lpActionsService = inject(LpActionsService);

  private triggers: Map<string, (() => Promise<void>)[]> = new Map();

  public async run(trigger: LpTrigger) {
    const callbacks = this.triggers.get(trigger.uid);
    if (!callbacks) return;

    for (const cb of callbacks) {
      await cb();
    }
  }

  public async on(listener: LpTriggerListener): Promise<() => void>;
  public async on(listeners: LpTriggerListener[]): Promise<() => void>;
  public async on(trigger: LpTrigger, event: LpEvent): Promise<() => void>;
  public async on(trigger: LpTrigger, cb: () => Promise<void>): Promise<() => void>;
  public async on(
    _listenerOrListenersOrTrigger: LpTriggerListener | LpTriggerListener[] | LpTrigger,
    _eventOrCb?: LpEvent | (() => Promise<void>),
  ): Promise<() => void> {
    if (Array.isArray(_listenerOrListenersOrTrigger)) {
      const listeners = _listenerOrListenersOrTrigger satisfies LpTriggerListener[];
      const offs = await Promise.all(listeners.map(async (listener) => await this.on(listener.on, listener.event)));
      return () => {
        for (const off of offs) {
          off();
        }
      };
    } else if (_listenerOrListenersOrTrigger.dataType === 'TriggerListener') {
      const listener = _listenerOrListenersOrTrigger satisfies LpTriggerListener;
      return await this.on(listener.on, listener.event);
    }

    const trigger = _listenerOrListenersOrTrigger satisfies LpTrigger;
    const cb = typeof _eventOrCb === 'function' ? _eventOrCb : async () => await this.#lpActionsService.perform(_eventOrCb);

    this.triggers.set(trigger.uid, [...(this.triggers.get(trigger.uid) ?? []), cb]);

    return () => this.off(trigger, cb);
  }

  public off(trigger: LpTrigger, cb: () => Promise<void>): void {
    const callbacks = this.triggers.get(trigger.uid);
    if (!callbacks) return;

    const index = callbacks.indexOf(cb);
    if (index !== -1) {
      callbacks.splice(index, 1);
      if (callbacks.length === 0) {
        this.triggers.delete(trigger.uid);
      } else {
        this.triggers.set(trigger.uid, callbacks);
      }
    }
  }
}
